import { Link } from "react-router-dom";
import { ArrowRight, Leaf, HeartPulse, ShieldCheck, Users } from "lucide-react";
import { BRAND } from "@/lib/constants";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Trust",
    body:
      "Lab-tested products, accurate labeling, and nothing on our shelves that we wouldn't recommend to our own families.",
  },
  {
    icon: Leaf,
    title: "Quality",
    body:
      "Curated Maryland-licensed growers and processors — sourced for potency, freshness, and reliability.",
  },
  {
    icon: HeartPulse,
    title: "Care",
    body:
      "Unrushed conversations, thoughtful dosing guidance, and respect for every visitor — medical or recreational.",
  },
  {
    icon: Users,
    title: "Community",
    body:
      "Rooted in Bethesda. We hire locally, partner locally, and show up for the neighborhood that supports us.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="py-16 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-4 md:gap-10 items-center">
            <div className="col-span-12 md:col-span-7 animate-fade-up">
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                About Us
              </p>
              <h1 className="mt-4 font-display text-[32px] leading-[1.1] sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
                A Bethesda dispensary built on{" "}
                <span className="text-[#28C0D8]">care &amp; compliance.</span>
              </h1>
              <p className="mt-6 text-[17px] leading-relaxed text-[#94A3B8] max-w-2xl">
                MD Supply Wellness was founded to bring a clinical-grade
                cannabis experience to Maryland — one that treats wellness as
                seriously as it deserves. We&apos;re preparing to open at{" "}
                {BRAND.address.line1} with a team of operators, caregivers, and
                cannabis specialists who believe the Bethesda community
                deserves better.
              </p>
            </div>
            <div className="col-span-12 md:col-span-5 animate-fade-up [animation-delay:120ms]">
              <div className="relative aspect-square md:aspect-[4/5] flex items-center justify-center p-6 md:p-10">
                <div
                  className="absolute inset-0 opacity-60 blur-3xl"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 45%, rgba(40,192,216,0.30) 0%, rgba(40,192,216,0.0) 65%)",
                  }}
                  aria-hidden="true"
                />
                <img
                  src={BRAND.logoTransparent}
                  alt="MD Supply Wellness logo"
                  className="relative w-full max-w-[360px] h-auto object-contain drop-shadow-[0_20px_60px_rgba(40,192,216,0.35)]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-28 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-4 md:gap-10">
            <div className="col-span-12 md:col-span-4">
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                Mission
              </p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight text-white">
                Reliable, safe, accessible.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-8 text-[17px] leading-relaxed text-[#CBD5E1]">
              <p>
                To provide reliable, safe, and accessible wellness supply
                solutions to the Bethesda community and the wider DMV — with
                an unwavering commitment to Maryland compliance, product
                integrity, and the well-being of every customer and patient
                who walks through our door.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-28 border-t border-white/5 bg-[#0A0B0E]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                Our Values
              </p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white max-w-2xl">
                Four principles we won&apos;t compromise on.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="group rounded-2xl border border-white/10 bg-[#13161C] p-7 hover:border-[#28C0D8]/50 hover:-translate-y-1 transition-all duration-300"
                  data-testid={`value-card-${i}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-[#28C0D8]/10 text-[#28C0D8] flex items-center justify-center mb-6 group-hover:bg-[#28C0D8] group-hover:text-[#0A0B0E] transition">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-white">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#94A3B8]">
                    {v.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-14 py-14 md:py-28 text-center">
          <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight max-w-3xl mx-auto">
            Want to see what we&apos;re building?
          </h3>
          <p className="mt-5 text-[15px] text-[#94A3B8] max-w-xl mx-auto">
            Visit our Shop Coming Soon page to join the waitlist, or check
            open roles.
          </p>
          <div className="mt-10 flex flex-col items-center sm:flex-row justify-center gap-3">
            <Link
              to="/menu"
              className="btn-teal-glow inline-flex items-center justify-center gap-2 rounded-full bg-[#28C0D8] px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC]"
            >
              Shop Coming Soon <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-full border border-white/15 px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-medium text-white hover:bg-white/5"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
